// Recognising a returning visitor by a first-party visitor id (docs/visitor-recognition.md),
// and the older fingerprint that is stored on every profile.

// ---------------------------------------------------------------------------- consent ----
// A magazine can be set to ask first (Admin: returning readers → ask first). Its banner
// (Components/ConsentBanner.razor) records the answer per visitor through this API:
//
//   window.magazineConsent.grant()     the visitor agreed
//   window.magazineConsent.decline()   the visitor said no: the banner doesn't come back
//   window.magazineConsent.revoke()    withdrawn: forgets the visitor id as well
//   window.magazineConsent.state()     'granted', 'declined' or null (not asked yet)
//   window.magazineConsent.open()      shows the banner again (the menu's cookie link)
// In a block, so these names stay out of the bundle's shared top-level scope.
{
    const CONSENT_KEY = 'magazine-consent';
    const VISITOR_KEY = 'magazine-visitor';
    const VISITOR_COOKIE = 'mv';

    const storage = {
        get: (key) => {
            try {
                return localStorage.getItem(key);
            } catch {
                return null;
            }
        },
        set: (key, value) => {
            try {
                localStorage.setItem(key, value);
            } catch {
                // private mode / blocked storage: the cookie still carries the visitor id
            }
        },
        remove: (key) => {
            try {
                localStorage.removeItem(key);
            } catch {
                // nothing stored, then
            }
        },
    };

    const cookie = {
        get: (name) => document.cookie.split('; ').find(c => c.startsWith(name + '='))?.slice(name.length + 1) ?? null,
        set: (name, value, maxAge) => {
            document.cookie = `${name}=${value}; Max-Age=${maxAge}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
        },
    };

    window.magazineConsent = {
        grant: () => storage.set(CONSENT_KEY, 'granted'),
        decline: () => {
            window.magazineConsent.revoke();
            storage.set(CONSENT_KEY, 'declined');
        },
        revoke: () => {
            storage.remove(CONSENT_KEY);
            storage.remove(VISITOR_KEY);
            cookie.set(VISITOR_COOKIE, '', 0);
        },
        granted: () => storage.get(CONSENT_KEY) === 'granted',
        state: () => storage.get(CONSENT_KEY),
        open: () => window.dispatchEvent(new Event('magazine-consent-open')),
        // the banner listens for open() through this; returns a function that stops listening
        listen: (banner) => {
            const handler = () => banner.invokeMethodAsync('Open');
            window.addEventListener('magazine-consent-open', handler);
            return { stop: () => window.removeEventListener('magazine-consent-open', handler) };
        },
    };

    // ---------------------------------------------------------------------- recognition ----
    // Called by Pages/Index.razor with the configured mode:
    //   off       returns null without touching storage at all
    //   consent   as `on`, but only after magazineConsent.grant()
    //   on        always
    //
    // Returns the first-party visitor id: a random id, created on first use and kept in
    // localStorage and a cookie on the magazine's own domain. Nothing about the device is
    // read. The server only ever stores a keyed hash of it.
    window.magazineVisitor = (mode) => {
        if (mode !== 'on' && !(mode === 'consent' && window.magazineConsent.granted())) {
            return null;
        }

        let id = storage.get(VISITOR_KEY) || cookie.get(VISITOR_COOKIE);
        if (!id || !/^[0-9a-f-]{36}$/.test(id)) {
            id = crypto.randomUUID();
        }
        // refresh both, so either one alone is enough to come back
        storage.set(VISITOR_KEY, id);
        cookie.set(VISITOR_COOKIE, id, 60 * 60 * 24 * 365);
        return id;
    };

    // ------------------------------------------------------------ device estimate ----
    // For statistics only: a hash of browser signals that stay the same between visits,
    // so read_session can estimate unique devices — including people who cleared storage
    // or read in a private window. Never used to recognise anyone: identical phones give
    // the same hash. Same gate as the visitor id. Called by js/magazine/tracking.js; the
    // server hashes it again with its own secret.
    window.magazineDevice = async (mode) => {
        if (mode !== 'on' && !(mode === 'consent' && window.magazineConsent.granted())) {
            return null;
        }
        try {
            return await sha((await stableSignals()).join('||'));
        } catch {
            return null;
        }
    };

    const sha = async (text) => {
        const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
        return [...new Uint8Array(bytes)].map(b => b.toString(16).padStart(2, '0')).join('');
    };

    // Left out on purpose, because they change on their own: window position, zoom
    // (devicePixelRatio), dark mode, IP address, browser version, Do Not Track.
    const stableSignals = async () => {
        const gpu = (() => {
            try {
                const gl = document.createElement('canvas').getContext('webgl');
                const info = gl?.getExtension('WEBGL_debug_renderer_info');
                return info ? `${gl.getParameter(info.UNMASKED_VENDOR_WEBGL)}|${gl.getParameter(info.UNMASKED_RENDERER_WEBGL)}` : '';
            } catch {
                return '';
            }
        })();

        // the same drawing comes out slightly different per GPU, driver, font set and OS
        const canvas = (() => {
            try {
                const c = document.createElement('canvas');
                c.width = 240;
                c.height = 60;
                const ctx = c.getContext('2d');
                ctx.textBaseline = 'top';
                ctx.font = '16px "Times New Roman", serif';
                ctx.fillStyle = '#f60';
                ctx.fillRect(100, 1, 62, 20);
                ctx.fillStyle = '#069';
                ctx.fillText('Magazine, ½ 🚗 €', 2, 15);
                ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
                ctx.beginPath();
                ctx.arc(200, 30, 20, 0, Math.PI * 2);
                ctx.fill();
                return c.toDataURL();
            } catch {
                return '';
            }
        })();

        const ua = navigator.userAgent;
        const browser = /Edg\//.test(ua) ? 'edge'
            : /OPR\//.test(ua) ? 'opera'
            : /Firefox\//.test(ua) ? 'firefox'
            : /Chrome\//.test(ua) ? 'chrome'
            : /Safari\//.test(ua) ? 'safari'
            : 'other';

        return [
            Intl.DateTimeFormat().resolvedOptions().timeZone || '',
            (navigator.languages || [navigator.language]).join(','),
            `${screen.width}x${screen.height}x${screen.colorDepth}`,
            String(navigator.hardwareConcurrency || ''),
            String(navigator.deviceMemory || ''),
            navigator.userAgentData?.platform || navigator.platform || '',
            String(navigator.maxTouchPoints || 0),
            browser,
            gpu ? await sha(gpu) : '',
            canvas ? await sha(canvas) : '',
        ];
    };
}

// ------------------------------------------------------------------ older fingerprint ----
// Hashed server-side together with the IP and Accept headers (Pages/Index.razor) and
// stored on every profile, as it always was. Not used for recognition: half of what goes
// into it changes between visits.
window.fp = () => {
    let data = [
        navigator.userAgent,
        navigator.language,
        screen.width,
        screen.height,
        screen.colorDepth,
        (navigator.platform || ''),
        (navigator.hardwareConcurrency || ''),
        (navigator.cookieEnabled ? '1' : '0'),
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        (typeof navigator.maxTouchPoints !== 'undefined' ? navigator.maxTouchPoints : ''),
        (navigator.doNotTrack || ''),
        (typeof window.sessionStorage !== 'undefined' ? 1 : 0),
        (typeof window.localStorage !== 'undefined' ? 1 : 0),
        (typeof window.indexedDB !== 'undefined' ? 1 : 0),
        (typeof window.openDatabase !== 'undefined' ? 1 : 0),
        (typeof window.CSS !== 'undefined' ? 1 : 0),
        (typeof window.WebGLRenderingContext !== 'undefined' ? 1 : 0),
        (typeof navigator.cpuClass !== 'undefined' ? navigator.cpuClass : ''),
        (typeof navigator.systemLanguage !== 'undefined' ? navigator.systemLanguage : ''),
        (typeof navigator.userLanguage !== 'undefined' ? navigator.userLanguage : ''),
        (typeof navigator.browserLanguage !== 'undefined' ? navigator.browserLanguage : ''),
        (navigator.vendor || ''),
        (navigator.pdfViewerEnabled || ''),
        (typeof window.matchMedia !== 'undefined' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'no-preference') : ''),
        (typeof document.body.style.webkitFilter !== 'undefined' ? 1 : 0),
        (typeof document.body.style.MozFilter !== 'undefined' ? 1 : 0),
        (typeof document.body.style.OFilter !== 'undefined' ? 1 : 0),
        (typeof document.body.style.filter !== 'undefined' ? 1 : 0),
        (typeof window.devicePixelRatio !== 'undefined' ? window.devicePixelRatio : ''),
        (typeof window.screenLeft !== 'undefined' ? window.screenLeft : ''),
        (navigator.deviceMemory || '')
    ].join('||');

    const props = (obj) => {
        const seen = new Set();
        let current = obj;

        while (current !== null) {
            Reflect.ownKeys(current).forEach(key => seen.add(key));
            current = Object.getPrototypeOf(current);
        }
        return seen.size;
    };

    data += '||' + props(navigator);

    return data;
};
